import { FC, ReactNode, useState } from 'react';
import { Col, Modal, Row } from 'antd';
import { TbAlignLeft } from 'react-icons/tb';

import { ResourceDraft, useDirtyResourceDraft } from '@/hooks/useResourceDrafts';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import { LearnMoreFooter } from '@/shared';
import { useInvestmentStore } from '@/stores';
import { InvestmentVisibilityType, SetStateMethod } from '@/types';

import CreateMoreControl from '../components/CreateMoreControl';
import ResourceCreationModalTitle from '../components/ResourceCreationModalTitle';
import { parseInvestmentDraftValues } from './investmentDraft';
import NewInvestment from './views/01-NewInvestment';

interface CreateInvestmentModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    initialDraft?: ResourceDraft<unknown> | null;
    onDraftResolved?: () => void;
}

const CreateInvestmentModal: FC<CreateInvestmentModalProps> = ({
    open,
    setOpen,
    initialDraft,
    onDraftResolved,
}): JSX.Element => {
    const initialDraftValues = parseInvestmentDraftValues(initialDraft?.values);
    const [segmentedValue] = useState('new');
    const [createMore, setCreateMore] = useState(false);
    const [creating, setCreating] = useState(false);
    const [visibility, setVisibility] = useState<InvestmentVisibilityType>(
        initialDraftValues.visibility ?? InvestmentVisibilityType.PRIVATE,
    );
    const [draftValues, setDraftValues] = useState({
        value: initialDraftValues.value ?? '',
        visibility,
    });
    const { investments } = useInvestmentStore();
    const createAccess = useWorkspaceResourceCreateAccess('investments', investments?.length ?? 0);

    const { dirtyDraftModal, markSubmitted, requestClose } = useDirtyResourceDraft({
        close: () => setOpen(false),
        draftId: initialDraft?.id,
        isDirty: draftValues.value.trim() !== '' || draftValues.visibility !== 'Private',
        resourceLabel: 'Investment',
        resourceType: 'investment',
        title: draftValues.value,
        values: draftValues,
    });

    const segmentedMap = {
        new: (
            <NewInvestment
                open={open}
                setOpen={setOpen}
                initialDraft={initialDraft}
                onCreated={() => {
                    markSubmitted();
                    onDraftResolved?.();
                }}
                onDraftValuesChange={setDraftValues}
                onCreatingChange={setCreating}
                createMore={createMore}
                visibility={visibility}
                setVisibility={setVisibility}
            />
        ),
        // integrations: <Integrations />,
        // templates: <Templates />,
    } as Record<string, ReactNode>;

    return (
        <>
            {dirtyDraftModal}
            <Modal
                afterOpenChange={(nextOpen) => {
                    if (nextOpen && initialDraft?.values) {
                        setVisibility(
                            parseInvestmentDraftValues(initialDraft.values).visibility ??
                                InvestmentVisibilityType.PRIVATE,
                        );
                    }
                }}
                mask={false}
                open={open}
                title={
                    <ResourceCreationModalTitle
                        disabled={creating}
                        icon={
                            <TbAlignLeft
                                size='1rem'
                                style={{ transform: 'rotate(-90deg)', verticalAlign: '-0.175rem' }}
                            />
                        }
                        title='Create Investment'
                        visibility={visibility}
                        setVisibility={(nextVisibility) => setVisibility(nextVisibility as InvestmentVisibilityType)}
                    />
                }
                closable={!creating}
                footer={
                    <Row>
                        <Col flex='none'>
                            <LearnMoreFooter align='left' topic='Investments' />
                        </Col>
                        <Col flex='auto' />
                        <Col flex='none'>
                            <CreateMoreControl
                                checked={createMore}
                                disabled={creating || createAccess.disabled}
                                onChange={setCreateMore}
                            />
                        </Col>
                    </Row>
                }
                onCancel={() => {
                    if (!creating) {
                        requestClose();
                    }
                }}
                style={{ minWidth: '50rem' }}
                bodyStyle={{ minHeight: '30rem' }}
            >
                {/* <Segmented
                block
                onChange={(e: any) => setSegmentedValue(e)}
                value={segmentedValue}
                options={[
                    {
                        value: 'new',
                        icon: <TbSearch size='1rem' style={{ verticalAlign: '-0.15rem' }} />,
                        label: 'Search',
                    },
                    {
                        disabled: true,
                        value: 'templates',
                        icon: <TbIcons size='1rem' style={{ verticalAlign: '-0.2rem' }} />,
                        label: <>Templates</>,
                    },
                ]}
            /> */}
                {segmentedMap[segmentedValue]}
            </Modal>
        </>
    );
};

export default CreateInvestmentModal;
